import { useState, useCallback, useEffect } from 'react';
import { useAppSelector } from '../../../../../../app/store';
import type { ServiceType, RoomType } from '../../../../../../services/ConfigurationService/types';

export interface ServiceAddFormData {
  priority: number;
  modifier: string;
  description: string;
  isPrimary: boolean;
  procedureStart: string;
  procedureEnd: string;
  procedureDuration: string;
  procedureTimeUnits: string;
  serviceCode: string;
  serviceDescription: string;
  room: string;
}

const createInitialFormData = (nextPriority: number): ServiceAddFormData => ({
  priority: nextPriority,
  modifier: '',
  description: '',
  isPrimary: nextPriority === 1,
  procedureStart: '',
  procedureEnd: '',
  procedureDuration: '',
  procedureTimeUnits: '',
  serviceCode: '',
  serviceDescription: '',
  room: '',
});

export const useServiceAddForm = () => {
  const encounter = useAppSelector(state => state.encounter.currentEncounter);
  
  // Calculate next priority based on existing procedures
  const nextPriority = (encounter?.procedures?.length ?? 0) + 1;
  
  const [formData, setFormData] = useState<ServiceAddFormData>(createInitialFormData(nextPriority));
  const [selectedService, setSelectedService] = useState<ServiceType | null>(null);
  const [selectedRoom, setSelectedRoom] = useState<RoomType | null>(null);
  const [error, setError] = useState('');

  // Calculate end time when start time or duration changes
  useEffect(() => {
    if (formData.procedureStart && formData.procedureDuration) {
      const startDate = new Date(formData.procedureStart);
      const durationMinutes = parseInt(formData.procedureDuration, 10);
      
      if (!isNaN(durationMinutes) && !isNaN(startDate.getTime())) {
        const endDate = new Date(startDate.getTime() + durationMinutes * 60 * 1000);
        const endDateStr = endDate.toISOString().slice(0, 16);
        setFormData(prev => ({ ...prev, procedureEnd: endDateStr }));
      }
    }
  }, [formData.procedureStart, formData.procedureDuration]);

  const handleInputChange = useCallback((field: keyof ServiceAddFormData, value: string | number | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  const handleServiceChange = useCallback((service: ServiceType | null) => {
    setSelectedService(service);
    if (service) {
      setFormData(prev => ({
        ...prev,
        serviceCode: service.code,
        serviceDescription: service.description,
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        serviceCode: '',
        serviceDescription: '',
      }));
    }
    // Clear error when user makes a selection
    if (error) setError('');
  }, [error]);

  const handleRoomChange = useCallback((room: RoomType | null) => {
    setSelectedRoom(room);
    if (room) {
      setFormData(prev => ({ ...prev, room: room.code }));
    } else {
      setFormData(prev => ({ ...prev, room: '' }));
    }
  }, []);

  const validateForm = useCallback((): boolean => {
    if (!selectedService) {
      setError('Please select a service from the dropdown');
      return false;
    }
    return true;
  }, [selectedService]);

  const resetForm = useCallback(() => {
    setFormData(createInitialFormData(nextPriority));
    setSelectedService(null);
    setSelectedRoom(null);
    setError('');
  }, [nextPriority]);

  return {
    formData,
    selectedService,
    selectedRoom,
    error,
    setError,
    handleInputChange,
    handleServiceChange,
    handleRoomChange,
    validateForm,
    resetForm,
  };
};
